import posthog from 'posthog-js'
import LogRocket from 'logrocket'
import { subscribe, cleanupCategories } from '@dacr/consent'
import type { Categories, Category, EffectiveState } from '@dacr/consent'

// The single, consent-gated tracker facade (§3.5). PostHog, GA and LogRocket
// load only on analytics consent; Meta only on advertising. No cookieless mode —
// PostHog is gated exactly like GA. Nothing is bootstrapped in index.html
// anymore, so this is the only path that can load a provider or emit an event.

const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY
const POSTHOG_HOST = 'https://hog.dacr.com'
const LOGROCKET_ID = import.meta.env.VITE_LOGROCKET_ID
const GA_ID = import.meta.env.VITE_GOOGLE_ANALYTICS_ID
const PIXEL_ID = import.meta.env.VITE_PIXEL_ID

const META_LOADER = `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');`

let posthogLoaded = false
let logRocketLoaded = false
let gaLoaded = false
let metaLoaded = false
let currentUserId: string | undefined
let consent: Categories = { analytics: false, advertising: false }

// The last known identity. The user is usually known (setUser) before consent
// resolves, and a grant must take effect immediately (§2.3), so identify can't
// be a one-shot gated call — it's buffered and (re)applied whenever an analytics
// provider loads under consent.
type IdentifyArgs = {
  userId: string
  traits?: Record<string, string | number | boolean>
}
let pendingIdentify: IdentifyArgs | null = null

const identifyLoadedProviders = (): void => {
  if (!pendingIdentify || !consent.analytics) return
  if (posthogLoaded) {
    posthog.identify(pendingIdentify.userId, pendingIdentify.traits)
  }
  if (logRocketLoaded) {
    LogRocket.identify(pendingIdentify.userId, pendingIdentify.traits)
  }
}

const runScript = (code: string): void => {
  const el = document.createElement('script')
  el.textContent = code
  document.head.appendChild(el)
}

// PostHog loads once, only on analytics consent — no cookieless mode, no
// mode-switch reload. Withdrawal unloads it via the controller's full-page
// reload, exactly like GA/LogRocket.
const loadPostHog = (): void => {
  if (posthogLoaded || !POSTHOG_KEY) return
  posthogLoaded = true
  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    defaults: '2026-05-30',
    person_profiles: 'always',
    // Stamp each event in this app, including the initial automatic pageview.
    // Persisted super properties could leak between website and web tabs.
    before_send: (event) => {
      if (event) event.properties = { ...event.properties, source: 'web' }
      return event
    },
  })
}

const loadLogRocket = (): void => {
  if (logRocketLoaded || !LOGROCKET_ID) return
  logRocketLoaded = true
  LogRocket.init(LOGROCKET_ID)
}

const loadGa = (): void => {
  if (gaLoaded || !GA_ID) return
  gaLoaded = true
  const tag = document.createElement('script')
  tag.async = true
  tag.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`
  document.head.appendChild(tag)
  const config = currentUserId
    ? `,{user_id:${JSON.stringify(currentUserId)}}`
    : ''
  runScript(
    `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments)}gtag('js',new Date());gtag('config',${JSON.stringify(GA_ID)}${config})`
  )
}

const loadMeta = (): void => {
  if (metaLoaded || !PIXEL_ID) return
  metaLoaded = true
  runScript(
    `${META_LOADER}fbq('init',${JSON.stringify(PIXEL_ID)});fbq('track','PageView')`
  )
}

// Rollout + continuous cleanup (§3.5): expire any legacy cookie for a category
// that is not currently granted. Idempotent — a grant re-writes its own.
const cleanupUngranted = (granted: Categories): void => {
  const denied: Category[] = []
  if (!granted.analytics) denied.push('analytics')
  if (!granted.advertising) denied.push('advertising')
  if (denied.length) cleanupCategories(denied)
}

const applyConsent = (state: EffectiveState): void => {
  consent = state.granted
  if (state.granted.analytics) {
    loadPostHog()
    loadGa()
    loadLogRocket()
    // Apply an identity that arrived before the grant (the boot race, or a live
    // accept while already signed in) — providers were just created.
    identifyLoadedProviders()
  }
  if (state.granted.advertising) loadMeta()
  cleanupUngranted(state.granted)
}

export const analytics = {
  /** Subscribe to consent and gate every provider off it. Call once at boot. */
  init() {
    subscribe(applyConsent)
  },

  identify(userId: string, traits?: Record<string, string | number | boolean>) {
    // Remember it regardless of consent; apply now if analytics is already
    // granted + loaded, else applyConsent() replays it on the grant.
    pendingIdentify = { userId, traits }
    identifyLoadedProviders()
  },

  track(event: string, properties?: Record<string, string | number | boolean>) {
    if (posthogLoaded) posthog.capture(event, properties)
    if (logRocketLoaded) LogRocket.track(event, properties)
  },

  page(name: string) {
    if (posthogLoaded) posthog.capture('$pageview', { page: name })
  },

  trackSignupConversion(userId: string) {
    if (gaLoaded) window.gtag('event', 'signup', { user_id: userId })
    if (metaLoaded) fbq('track', 'Lead')
  },

  trackLogin(userId: string) {
    if (gaLoaded) window.gtag('event', 'login', { user_id: userId })
  },

  configurePageTracking(userId?: string) {
    currentUserId = userId
    if (gaLoaded) {
      window.gtag('config', GA_ID, ...(userId ? [{ user_id: userId }] : []))
    }
  },

  captureException(
    error: Error,
    context?: Record<string, string | number | boolean>
  ) {
    if (logRocketLoaded) LogRocket.captureException(error, { extra: context })
  },

  captureGraphQLError(operationName: string, errors: readonly unknown[]) {
    if (!logRocketLoaded) return
    LogRocket.captureException(new Error(`GraphQL: ${operationName}`), {
      extra: { errors: JSON.stringify(errors) },
    })
  },

  reset() {
    pendingIdentify = null
    if (posthogLoaded) posthog.reset()
    if (logRocketLoaded) LogRocket.startNewSession()
  },
}
